import React, { useState, useEffect } from 'react';
import api from '../services/api';
import AuditLogTable from '../components/safety/AuditLogTable';
import PendingActionsBanner from '../components/safety/PendingActionsBanner';
import DashboardContainer from '../components/layout/DashboardContainer';
import { useNotification } from '../context/NotificationContext';
import { BrainCircuit, MessageSquare, Bot, Sparkles, Check, X } from 'lucide-react';
import './AISettingsPage.css';

function AISettingsPage() {
    const { showSuccess, showError } = useNotification();
    const [settings, setSettings] = useState(null);
    const [pendingActions, setPendingActions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [settingsRes, actionsRes] = await Promise.all([
                api.get('/ai/settings'),
                api.get('/actions/pending')
            ]);
            setSettings(settingsRes.data);
            setPendingActions(actionsRes.data);
        } catch (err) {
            console.error("Failed to load AI settings data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/ai/settings', settings);
            showSuccess('AI settings saved successfully.');
        } catch (err) {
            console.error("Failed to save settings", err);
            showError(err.response?.data?.detail || 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmAction = async (actionId) => {
        try {
            await api.post(`/actions/${actionId}/confirm`);
            // Reload pending actions to remove the confirmed one
            const { data } = await api.get('/actions/pending');
            setPendingActions(data);
        } catch (err) {
            console.error("Failed to confirm action", err);
            showError(err.response?.data?.detail || 'Failed to confirm action.');
        }
    };

    const handleCancelAction = async (actionId) => {
        try {
            await api.post(`/actions/${actionId}/cancel`);
            // Reload pending actions to remove the cancelled one
            const { data } = await api.get('/actions/pending');
            setPendingActions(data);
        } catch (err) {
            console.error("Failed to cancel action", err);
            showError(err.response?.data?.detail || 'Failed to cancel action.');
        }
    };

    if (loading) {
        return (
            <DashboardContainer>
                <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading AI Settings...</div>
                </div>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer>
            <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
                <div className="page-toolbar">
                    <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h1 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px 0' }}>
                                <BrainCircuit size={24} /> AI Settings & Safety
                            </h1>
                            <p style={{ margin: 0, color: '#666' }}>Configure your AI assistant's behavior and review its actions.</p>
                        </div>
                        <div className="page-header-actions">
                            <button className="btn primary" onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', fontWeight: '500' }}>
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>

                <PendingActionsBanner
                    actions={pendingActions}
                    onConfirm={handleConfirmAction}
                    onCancel={handleCancelAction}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginTop: '20px' }}>
                    <div className="card padded">
                        <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Inbound Mode</h3>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>How should the system handle incoming customer messages?</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                className={`mode-btn ${settings?.inbound_mode === 'manual' ? 'active' : ''}`}
                                onClick={() => setSettings(s => ({ ...s, inbound_mode: 'manual' }))}
                            >
                                <div className="mode-icon"><MessageSquare size={20} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 600, color: '#333' }}>Manual Response</span>
                                    <span style={{ fontSize: '13px', color: '#666' }}>You reply to all messages. AI is off.</span>
                                </div>
                            </button>
                            <button
                                className={`mode-btn ${settings?.inbound_mode === 'bot' ? 'active' : ''}`}
                                onClick={() => setSettings(s => ({ ...s, inbound_mode: 'bot' }))}
                            >
                                <div className="mode-icon"><Bot size={20} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 600, color: '#333' }}>Bot Flows</span>
                                    <span style={{ fontSize: '13px', color: '#666' }}>Uses pre-defined rules. No AI generation.</span>
                                </div>
                            </button>
                            <button
                                className={`mode-btn ${settings?.inbound_mode === 'ai' ? 'active' : ''}`}
                                onClick={() => setSettings(s => ({ ...s, inbound_mode: 'ai' }))}
                            >
                                <div className="mode-icon"><Sparkles size={20} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 600, color: '#333' }}>AI Agent</span>
                                    <span style={{ fontSize: '13px', color: '#666' }}>AI reads the Knowledge Base and responds naturally.</span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {settings?.inbound_mode === 'ai' && (
                        <div className="card padded">
                            <h3 style={{ marginTop: 0, marginBottom: '8px' }}>AI Configuration</h3>
                            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>Customize how your AI assistant behaves and responds.</p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Response Style</label>
                                    <select
                                        className="select"
                                        value={settings?.response_style || 'balanced'}
                                        onChange={e => setSettings(s => ({ ...s, response_style: e.target.value }))}
                                        style={{ width: '100%', maxWidth: '300px' }}
                                    >
                                        <option value="friendly">Friendly — Casual, warm tone</option>
                                        <option value="balanced">Balanced — Professional yet approachable</option>
                                        <option value="formal">Formal — Strictly professional</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Greeting Message</label>
                                    <span style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px' }}>Sent to new customers when AI starts a conversation.</span>
                                    <textarea
                                        className="input"
                                        rows={3}
                                        value={settings?.greeting_message || ''}
                                        onChange={e => setSettings(s => ({ ...s, greeting_message: e.target.value }))}
                                        placeholder="Hello! How can I help you today?"
                                        style={{ width: '100%', resize: 'vertical' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Escalation Message</label>
                                    <span style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '6px' }}>Sent to customers when the AI can't answer and escalates to you.</span>
                                    <textarea
                                        className="input"
                                        rows={3}
                                        value={settings?.escalation_message || ''}
                                        onChange={e => setSettings(s => ({ ...s, escalation_message: e.target.value }))}
                                        placeholder="I'll connect you with our team. Someone will respond shortly!"
                                        style={{ width: '100%', resize: 'vertical' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card padded">
                        <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Safety Controls</h3>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>Configure constraints for the AI Agent.</p>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <strong style={{ color: '#333' }}>Auto-Escalate Unknowns</strong>
                                <span style={{ fontSize: '13px', color: '#666' }}>If the AI doesn't know the answer, flag it for your review instead of guessing.</span>
                            </div>
                            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={settings?.auto_escalate ?? true}
                                    onChange={e => setSettings(s => ({ ...s, auto_escalate: e.target.checked }))}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                            </label>
                        </div>
                    </div>
                </div>

                <div>
                    <AuditLogTable />
                </div>
            </div>
        </DashboardContainer>
    );
}

export default AISettingsPage;
